/**
 * streetlights.ts — seeded streetlight placement for W2 settlements. Pure/
 * deterministic (salt 0x51a7): interval placement along non-tunnel road edges
 * with per-lamp jitter (spacing, side, height, variant), plus extra lamps at
 * junction (degree ≥ 3) and seed/port nodes. Grounded on the carved surface via
 * the sampler (which carries the settlement index in production). pmath-only.
 */
import * as pmath from "../pmath.js";
import { hashSeed } from "../rng.js";
import { hashInts, h01 } from "./hash.js";
import { packCell } from "./cells.js";
import {
  type CityGraph, localToDir, EDGE_TUNNEL, NODE_JUNCTION, NODE_SEED, NODE_PORT,
} from "./citygraph.js";
import type { PlanetSampler } from "../universe.js";

export const STREETLIGHT_STRIDE = 6;
/** Nominal interval along a road edge (metres). Raised 18→36 to halve density. */
export const STREETLIGHT_SPACING_M = 36;
export const STREETLIGHT_HEIGHT_M = 4.5;
export const STREETLIGHT_VARIANTS = 3;
/** Clearance from the road EDGE to the lamp base (metres) — the lamp sits this
 *  far BEYOND the carriageway so it is never in the road. */
export const STREETLIGHT_MARGIN_M = 1.6;
const SALT = 0x51a7;

export function buildStreetlights(g: CityGraph, sampler: PlanetSampler): Float64Array {
  const frame = g.frame;
  const R = sampler.def.radius;
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(g.regionId);
  const out: number[] = [];
  const d: number[] = [0, 0, 0];

  // `_salt`/`_key` are accepted for call-site self-documentation of the stream
  // domain but unused here (the hashes are drawn at the call sites); underscore-
  // prefixed so noUnusedParameters stays satisfied without changing behaviour.
  const groundAt = (u: number, v: number, _salt: number, _key: number, sub: number, height: number): void => {
    localToDir(frame, u, v, d);
    const h = sampler.height(d[0], d[1], d[2]);
    const baseR = R + h;
    out.push(d[0] * baseR, d[1] * baseR, d[2] * baseR, 0, height, sub % STREETLIGHT_VARIANTS);
  };

  // Lamp base offset from a road CENTRELINE = the edge's half-width + margin, so
  // the lamp always sits just BEYOND the carriageway, never in it. edgeWidth is
  // the resolved per-edge width (v11); fall back to ~5 m (a street) when unset.
  const offsetOf = (e: number): number =>
    (g.edgeWidth[e] > 0 ? g.edgeWidth[e] : 5) * 0.5 + STREETLIGHT_MARGIN_M;

  // ── interval pass: step along each non-tunnel road edge ──
  const degree = new Int32Array(g.nodeU.length);
  const firstEdge = new Int32Array(g.nodeU.length).fill(-1);
  for (let e = 0; e < g.edgeKind.length; e++) {
    if (g.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    degree[a]++; degree[b]++;
    if (firstEdge[a] < 0) firstEdge[a] = e;
    if (firstEdge[b] < 0) firstEdge[b] = e;
    const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
    const len = pmath.hypot(bu - au, bv - av);
    if (len < STREETLIGHT_SPACING_M) continue;
    const steps = Math.floor(len / STREETLIGHT_SPACING_M);
    const off = offsetOf(e);
    for (let s = 1; s < steps; s++) {
      // seeded jitter per (edge, step): keep/skip, t-offset, side, height, variant
      const roll = hashInts(planetHash, SALT, rk, e, s);
      if (h01(roll) < 0.15) continue; // occasional gap
      const jitterT = (h01(hashInts(planetHash, SALT, rk, e, s * 4 + 1)) - 0.5) * 0.4;
      const t = Math.min(0.98, Math.max(0.02, (s + jitterT) / steps));
      const side = h01(hashInts(planetHash, SALT, rk, e, s * 4 + 2)) < 0.5 ? 1 : -1;
      const hj = 0.85 + h01(hashInts(planetHash, SALT, rk, e, s * 4 + 3)) * 0.4;
      // point along the edge, offset perpendicular by the road half-width + margin
      const du = bu - au, dv = bv - av;
      const pu = au + du * t, pv = av + dv * t;
      const nu = -dv / len, nv = du / len; // unit perpendicular in chart space
      groundAt(pu + nu * side * off, pv + nv * side * off, SALT, e, s, STREETLIGHT_HEIGHT_M * hj);
    }
  }

  // ── junction pass: ONE lamp at ~half of the degree-≥3 / seed / port hubs
  //    (50% presence gate keeps density halved). Placed a few metres along the
  //    hub's first incident edge, offset perpendicular by that edge's half-width
  //    + margin — so junction lamps also clear the road, never sit in it. ──
  for (let nnode = 0; nnode < g.nodeU.length; nnode++) {
    const isHub = degree[nnode] >= 3 || g.nodeKind[nnode] === NODE_SEED
      || g.nodeKind[nnode] === NODE_PORT || g.nodeKind[nnode] === NODE_JUNCTION;
    if (!isHub || degree[nnode] === 0) continue;
    if (h01(hashInts(planetHash, SALT, rk, 0x9000 + nnode, 0)) < 0.5) continue; // 50% gate
    const fe = firstEdge[nnode];
    if (fe < 0) continue;
    // direction from the node ALONG its first incident edge, + perpendicular
    const other = g.edgeA[fe] === nnode ? g.edgeB[fe] : g.edgeA[fe];
    let du = g.nodeU[other] - g.nodeU[nnode], dv = g.nodeV[other] - g.nodeV[nnode];
    const l = pmath.hypot(du, dv) || 1; du /= l; dv /= l;
    const side = h01(hashInts(planetHash, SALT, rk, 0x9000 + nnode, 1)) < 0.5 ? 1 : -1;
    const off = offsetOf(fe);
    const along = 3.0; // metres from the node toward the edge
    const hj = 0.9 + h01(hashInts(planetHash, SALT, rk, 0x9000 + nnode, 2)) * 0.3;
    groundAt(
      g.nodeU[nnode] + du * along + (-dv) * side * off,
      g.nodeV[nnode] + dv * along + du * side * off,
      SALT, nnode, 0, STREETLIGHT_HEIGHT_M * hj);
  }
  return new Float64Array(out);
}
